import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit2, Trash2, Inbox, LogOut, Globe, FileText, Eye, EyeOff, Sparkles, Users, Mail, Send, ShieldCheck, AlertTriangle, ClipboardList, Bot } from "lucide-react";
import { toast } from "sonner";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useAuth } from "@/contexts/AuthContext";

const STATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "pending_review", label: "Pending Review" },
  { key: "published", label: "Published" },
];

const STATE_STYLE = {
  draft: "bg-[#EAEAEA] text-black",
  pending_review: "bg-[#FFE08A] text-black",
  published: "bg-[#008A00] text-white",
};

export default function AdminDashboard() {
  const [entries, setEntries] = useState([]);
  const [pendingTips, setPendingTips] = useState(0);
  const [auditBelow, setAuditBelow] = useState(0);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const navigate = useNavigate();
  const config = usePublicConfig();
  const editorEmail = config.editorial_contact_email;
  const { signOut } = useAuth();
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (tab !== "all") params.review_state = tab;
      const [eRes, tRes, aRes] = await Promise.all([
        api.get("/admin/entries", { params }),
        api.get("/tips", { params: { status: "pending" } }),
        api.get("/admin/audit/citations"),
      ]);
      setEntries(eRes.data);
      setPendingTips(tRes.data.length);
      setAuditBelow(aRes.data.total_below_threshold || 0);
    } catch (err) {
      console.error("Failed to load admin data", err);
      toast.error("Failed to load admin data");
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const filtered = entries.filter((e) => !q || e.title.toLowerCase().includes(q.toLowerCase()));

  const counts = {
    all: entries.length,
    draft: entries.filter((e) => e.review_state === "draft").length,
    pending_review: entries.filter((e) => e.review_state === "pending_review").length,
    published: entries.filter((e) => e.review_state === "published").length,
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry permanently?")) return;
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Deleted");
      setEntries((es) => es.filter((e) => e.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const unpublish = async (id) => {
    if (!window.confirm("Unpublish this entry? It will return to drafts.")) return;
    try {
      await api.post(`/entries/${id}/unpublish`);
      toast.success("Unpublished — back to drafts");
      load();
    } catch { toast.error("Unpublish failed"); }
  };

  const submitReview = async (id) => {
    try {
      await api.post(`/entries/${id}/submit-for-review`);
      toast.success("Submitted for review");
      load();
    } catch { toast.error("Submit failed"); }
  };

  const logout = async () => {
    await signOut();
    navigate("/admin/login");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black/15 pb-6 mb-6">
        <div>
          <div className="overline">/editor desk</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Admin Dashboard</h1>
          <p className="text-sm text-[#555] mt-2 max-w-xl">
            Drafts and pending entries are visible only to editors. Publish requires every citation to be marked verified by a human reviewer.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/editors">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-editors-btn">
              <Users className="w-4 h-4 mr-2" /> Editors
            </Button>
          </Link>
          <Link to="/admin/digest">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-digest-btn">
              <Send className="w-4 h-4 mr-2" /> Digest
            </Button>
          </Link>
          <Link to="/admin/tips">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-tips-btn">
              <Inbox className="w-4 h-4 mr-2" /> Tip queue {pendingTips > 0 && <span className="ml-2 bg-[#D32F2F] text-white px-2 py-0.5 text-xs">{pendingTips}</span>}
            </Button>
          </Link>
          <Link to="/admin/audit">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-audit-btn">
              <AlertTriangle className="w-4 h-4 mr-2" /> Audit
              {auditBelow > 0 && <span className="ml-2 bg-[#D32F2F] text-white px-2 py-0.5 text-xs">{auditBelow}</span>}
            </Button>
          </Link>
          <Link to="/admin/audit-log">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-audit-log-btn">
              <ClipboardList className="w-4 h-4 mr-2" /> Audit log
            </Button>
          </Link>
          <Link to="/admin/editor-stats">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-editor-stats-btn">
              <Users className="w-4 h-4 mr-2" /> Editor stats
            </Button>
          </Link>
          <Link to="/admin/ingest">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-ingest-btn">
              <Bot className="w-4 h-4 mr-2" /> Daily ingest
            </Button>
          </Link>
          <Link to="/admin/agent">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-agent-btn">
              <Bot className="w-4 h-4 mr-2" /> AI Agent
            </Button>
          </Link>
          <Link to="/admin/2fa">
            <Button variant="outline" className="rounded-none border-black h-11" data-testid="goto-2fa-btn">
              <ShieldCheck className="w-4 h-4 mr-2" /> 2FA
            </Button>
          </Link>
          <Link to="/admin/new">
            <Button className="rounded-none bg-black text-white h-11" data-testid="new-entry-btn">
              <Plus className="w-4 h-4 mr-2" /> New Entry
            </Button>
          </Link>
          <Button variant="outline" onClick={logout} className="rounded-none border-black/40 h-11" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      {editorEmail && (
        <div className="mb-6 border border-black/15 bg-[#FFF8E1] p-3 flex items-center gap-2 flex-wrap">
          <Mail className="w-4 h-4 shrink-0" />
          <span className="text-sm">
            <b>Editorial contact:</b>{" "}
            <a href={`mailto:${editorEmail}`} className="underline underline-offset-4" data-testid="editorial-contact-link">{editorEmail}</a>
            {" · "}use this address for review requests, corrections, and tips.
          </span>
        </div>
      )}

      {/* State counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/15 border border-black/15 mb-6">
        {STATE_FILTERS.map((s) => (
          <button
            key={s.key}
            data-testid={`state-tab-${s.key}`}
            onClick={() => setTab(s.key)}
            className={`p-4 text-left transition ${tab === s.key ? "bg-black text-white" : "bg-white hover:bg-[#FAFAFA]"}`}
          >
            <div className="font-mono-plex text-[11px] uppercase tracking-wider opacity-70">{s.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{counts[s.key] || 0}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-black/15">
        <div className="p-3 border-b border-black/10">
          <Input data-testid="admin-search" placeholder="Search entries…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none border-black/20 h-10" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F9] border-b border-black/10">
              <tr className="font-mono-plex uppercase text-[11px] tracking-[0.2em] text-[#555]">
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">State</th>
                <th className="text-left p-3">Citations</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => {
                const cites = e.citations || [];
                const verifiedN = cites.filter((c) => c.verified).length;
                const allVerified = cites.length > 0 && verifiedN === cites.length;
                return (
                <tr key={e.id} className={`${i % 2 ? "bg-[#FAFAFA]" : "bg-white"} border-b border-black/5`} data-testid={`admin-row-${e.id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-2">
                      {e.ai_assisted && <Sparkles className="w-3.5 h-3.5 text-[#0050FF]" title="AI-assisted draft" />}
                      <span>{e.title}</span>
                    </div>
                    <div className="font-mono-plex text-[11px] text-[#888] mt-0.5 uppercase tracking-wider">
                      {e.created_by ? `by ${e.created_by}` : "—"}{e.reviewed_by ? ` · reviewed ${e.reviewed_by}` : ""}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`font-mono-plex text-[11px] uppercase tracking-wider px-2 py-1 ${STATE_STYLE[e.review_state] || "bg-[#EAEAEA]"}`} data-testid={`state-badge-${e.id}`}>
                      {formatLabel(e.review_state || "draft")}
                    </span>
                  </td>
                  <td className="p-3 font-mono-plex text-xs">
                    <span className={allVerified ? "text-[#008A00]" : "text-[#D32F2F]"}>
                      {verifiedN}/{cites.length} verified
                    </span>
                  </td>
                  <td className="p-3">{formatLabel(e.category)}</td>
                  <td className="p-3 font-mono-plex text-xs">{e.date_action}</td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    {e.review_state === "published" && (
                      <Link to={`/entry/${e.id}`} className="inline-block">
                        <Button variant="outline" className="rounded-none border-black/30 h-8 px-2 text-xs" data-testid={`view-${e.id}`}><Eye className="w-3 h-3" /></Button>
                      </Link>
                    )}
                    {e.review_state === "draft" && (
                      <Button onClick={() => submitReview(e.id)} variant="outline" className="rounded-none border-[#0050FF] text-[#0050FF] h-8 px-2 text-xs" data-testid={`submit-review-${e.id}`} title="Submit for review">
                        <FileText className="w-3 h-3" />
                      </Button>
                    )}
                    {e.review_state === "pending_review" && editorEmail && (
                      <a
                        href={`mailto:${editorEmail}?subject=${encodeURIComponent("Receipts — please review draft: " + e.title)}&body=${encodeURIComponent("A draft is awaiting your review:\n\n" + e.title + "\n\nReview at: " + PUBLIC_URL + "/admin/edit/" + e.id + "\n\nDrafted by: " + (e.created_by || "—") + "\nCitations: " + (e.citations || []).filter((c) => c.verified).length + "/" + (e.citations || []).length + " verified")}`}
                        className="inline-block"
                        title="Email a reviewer"
                        data-testid={`notify-reviewer-${e.id}`}
                      >
                        <Button variant="outline" className="rounded-none border-[#FFAA00] text-[#7a5500] h-8 px-2 text-xs">
                          <Mail className="w-3 h-3" />
                        </Button>
                      </a>
                    )}
                    {e.review_state === "published" && (
                      <Button onClick={() => unpublish(e.id)} variant="outline" className="rounded-none border-black/30 h-8 px-2 text-xs" data-testid={`unpublish-${e.id}`} title="Unpublish">
                        <EyeOff className="w-3 h-3" />
                      </Button>
                    )}
                    <Link to={`/admin/edit/${e.id}`} className="inline-block">
                      <Button variant="outline" className="rounded-none border-black/30 h-8 px-2 text-xs" data-testid={`edit-${e.id}`}><Edit2 className="w-3 h-3" /></Button>
                    </Link>
                    <Button variant="outline" onClick={() => del(e.id)} className="rounded-none border-[#D32F2F] text-[#D32F2F] h-8 px-2 text-xs" data-testid={`delete-${e.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              );})}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-[#555]">No entries.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
